// JOBE-0053.8: thin client for the JOBE-0053.6 WebSocket bridge at
// /api/ws.
//
// Frame shapes (see specs/completed/JOBE-0053.6-bff-websocket-bridge.md):
//
//   inbound (client -> server):
//     {type: "subscribe",   channel_id, corr_id?}
//     {type: "unsubscribe", channel_id, corr_id?}
//     {type: "mark_read",   channel_id, thread_urn, last_read_seq, corr_id?}
//
//   outbound (server -> client):
//     {type: "subscribed",     channel_id, corr_id?}
//     {type: "unsubscribed",   channel_id, corr_id?}
//     {type: "event",          channel_id, event: {...ConversationEvent}}
//     {type: "mark_read_ack",  channel_id, corr_id?, cursor: {...ReadCursor}}
//     {type: "error",          code, message, corr_id?}
//
// This client is intentionally small: a single shared connection per
// tab, refcounted, with reconnect-on-close + replay of subscribed
// channels. The Chat.jsx route is the only consumer for now.

let ws = null;
let openCount = 0;
let reconnectTimer = null;
let reconnectDelayMs = 1000;
const RECONNECT_MAX_MS = 30 * 1000;

// Channels the SPA wants subscribed. Survives reconnects so
// dropping the WS doesn't lose interest in those channels.
const desiredChannels = new Set();
// Channels currently subscribed on the live ws (server-acked).
const subscribedChannels = new Set();
// {channel_id -> Set<handler>} — fired on every inbound `event` frame
// for that channel. handler(event) where event is the
// ConversationEvent the bridge forwarded from JetStream.
const eventListeners = new Map();
// {channel_id -> Set<handler>} — fired on each subscribed/unsubscribed
// ack so the SPA can drive UI loading state.
const ackListeners = new Map();
// Status listeners for connection state ('connecting'|'open'|'closed').
const statusListeners = new Set();
let lastStatus = 'closed';

function notifyStatus(s) {
  if (s === lastStatus) return;
  lastStatus = s;
  for (const fn of statusListeners) {
    try { fn(s); } catch { /* swallow */ }
  }
}

function fireEvent(channelID, payload) {
  const set = eventListeners.get(channelID);
  if (!set) return;
  for (const fn of set) {
    try { fn(payload); } catch { /* swallow */ }
  }
}

function fireAck(channelID, type) {
  const set = ackListeners.get(channelID);
  if (!set) return;
  for (const fn of set) {
    try { fn(type); } catch { /* swallow */ }
  }
}

function send(frame) {
  if (!ws || ws.readyState !== WebSocket.OPEN) return false;
  try {
    ws.send(JSON.stringify(frame));
    return true;
  } catch {
    return false;
  }
}

function openWS() {
  if (ws) return;
  if (typeof WebSocket === 'undefined') return;
  notifyStatus('connecting');
  // Same-origin upgrade. The BFF reverse-proxies /api/ws to the
  // operator's bridge handler.
  const proto = (typeof window !== 'undefined' && window.location.protocol === 'https:') ? 'wss:' : 'ws:';
  const host = (typeof window !== 'undefined') ? window.location.host : '';
  ws = new WebSocket(`${proto}//${host}/api/ws`);
  ws.addEventListener('open', () => {
    notifyStatus('open');
    reconnectDelayMs = 1000;
    // Replay subscribes for channels we still want.
    subscribedChannels.clear();
    for (const ch of desiredChannels) {
      send({ type: 'subscribe', channel_id: ch });
    }
  });
  ws.addEventListener('message', (msg) => {
    let frame;
    try { frame = JSON.parse(msg.data); }
    catch { return; }
    if (!frame || typeof frame !== 'object') return;
    switch (frame.type) {
      case 'subscribed':
        if (frame.channel_id) {
          subscribedChannels.add(frame.channel_id);
          fireAck(frame.channel_id, 'subscribed');
        }
        break;
      case 'unsubscribed':
        if (frame.channel_id) {
          subscribedChannels.delete(frame.channel_id);
          fireAck(frame.channel_id, 'unsubscribed');
        }
        break;
      case 'event':
        if (frame.channel_id && frame.event) fireEvent(frame.channel_id, frame.event);
        break;
      case 'mark_read_ack':
        // Ignored by default — the inbox projection refresh path
        // is the canonical truth. Surface via ackListeners for
        // tests / future use.
        if (frame.channel_id) fireAck(frame.channel_id, 'mark_read_ack');
        break;
      case 'error':
        // Bridge surfaces unauthorized / mark_read_denied / etc. We
        // don't surface to UI today; future work can add a global
        // error sink.
        break;
      default:
        break;
    }
  });
  const onClose = () => {
    notifyStatus('closed');
    ws = null;
    subscribedChannels.clear();
    if (openCount > 0) {
      // Reconnect with exponential backoff, capped.
      if (reconnectTimer) clearTimeout(reconnectTimer);
      const delay = reconnectDelayMs;
      reconnectDelayMs = Math.min(RECONNECT_MAX_MS, Math.floor(reconnectDelayMs * 1.7));
      reconnectTimer = setTimeout(() => { reconnectTimer = null; openWS(); }, delay);
    }
  };
  ws.addEventListener('close', onClose);
  ws.addEventListener('error', () => { /* close fires after error */ });
}

// startWS opens the shared WS on first call and refcounts. Returns
// a stop function that the caller invokes on unmount.
export function startWS() {
  openCount += 1;
  if (!ws) openWS();
  let stopped = false;
  return function stopWS() {
    if (stopped) return;
    stopped = true;
    openCount -= 1;
    if (openCount > 0) return;
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    if (ws) {
      try { ws.close(); } catch { /* ignore */ }
      ws = null;
    }
    notifyStatus('closed');
  };
}

// subscribeChannel adds a server subscribe and registers `onEvent`
// for inbound event frames. Returns an unsubscribe function that
// removes the listener (and unsubscribes server-side when the last
// listener for a channel goes away).
export function subscribeChannel(channelID, onEvent) {
  if (!channelID) return () => {};
  if (!eventListeners.has(channelID)) eventListeners.set(channelID, new Set());
  eventListeners.get(channelID).add(onEvent);
  const wasDesired = desiredChannels.has(channelID);
  desiredChannels.add(channelID);
  if (!wasDesired) {
    // Send subscribe; if the WS isn't open yet, the open handler will
    // replay desiredChannels.
    send({ type: 'subscribe', channel_id: channelID });
  }
  return function unsubscribe() {
    const set = eventListeners.get(channelID);
    if (!set) return;
    set.delete(onEvent);
    if (set.size === 0) {
      eventListeners.delete(channelID);
      desiredChannels.delete(channelID);
      send({ type: 'unsubscribe', channel_id: channelID });
    }
  };
}

// markRead sends a mark_read frame for (channel, thread, seq). No-op
// when the WS isn't open — the next inbox/projection refresh will
// reconcile if the SPA missed the round-trip.
export function markRead(channelID, threadURN, lastReadSeq) {
  if (!channelID || !threadURN) return false;
  // Coerce to a non-negative integer without going through `| 0`, which
  // truncates to signed 32-bit. JetStream stream sequences exceed 2^31
  // and the BFF unmarshals last_read_seq as uint64 — a negative or
  // wrapped value would fail decode on the wire.
  const seq = Math.max(0, Math.floor(Number(lastReadSeq) || 0));
  return send({
    type: 'mark_read',
    channel_id: channelID,
    thread_urn: threadURN,
    last_read_seq: seq,
  });
}

// onStatus subscribes to connection-state transitions
// ('connecting'|'open'|'closed'). Fires immediately with the current
// status.
export function onStatus(fn) {
  statusListeners.add(fn);
  try { fn(lastStatus); } catch { /* swallow */ }
  return () => { statusListeners.delete(fn); };
}

// Test helpers — exported under a leading underscore so production
// code never imports them.
export const _internals = {
  reset() {
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    if (ws) { try { ws.close(); } catch { /* ignore */ } ws = null; }
    openCount = 0;
    desiredChannels.clear();
    subscribedChannels.clear();
    eventListeners.clear();
    ackListeners.clear();
    statusListeners.clear();
    lastStatus = 'closed';
    reconnectDelayMs = 1000;
  },
};
